import { useLayoutEffect, useRef, useState } from "react";
import { aabb, snap } from "@/lib/gangsheet";
import { DEFAULT_FILM } from "@/components/shirts/gang/useGangSheet";

function Ruler({ ppi, lengthIn, printW }) {
    const xTicks = [];
    for (let i = 0; i <= Math.ceil(printW); i++) xTicks.push(i);
    const yTicks = [];
    for (let i = 0; i <= Math.ceil(lengthIn); i += lengthIn > 60 ? 6 : 2) yTicks.push(i);
    return (
        <>
            <div className="absolute -top-6 left-0 h-6" style={{ width: printW * ppi }}>
                {xTicks.map((t) => (
                    <div key={t} className="absolute bottom-0 flex flex-col items-center" style={{ left: t * ppi }}>
                        <span className="font-mono text-[11px] text-zinc-500">{t}</span>
                        <span className="h-1.5 w-px bg-black/25" />
                    </div>
                ))}
            </div>
            <div className="absolute -left-9 top-0 w-9" style={{ height: lengthIn * ppi }}>
                {yTicks.map((t) => (
                    <div key={t} className="absolute right-0 flex items-center gap-1" style={{ top: t * ppi - 5 }}>
                        <span className="font-mono text-[11px] text-zinc-500">{t % 12 === 0 && t > 0 ? `${t / 12}′` : t}</span>
                        <span className="h-px w-1.5 bg-black/25" />
                    </div>
                ))}
            </div>
        </>
    );
}

export default function SheetStage({
    items, selected, setSelected, lengthIn, zoom, onFitChange,
    conflicts = [], oob = [], onLiveChange, onDragStart, film = DEFAULT_FILM,
}) {
    const SHEET_WIDTH_IN = film.width_inches;
    const PRINTABLE_WIDTH_IN = film.printable_width_inches;
    const SPACING_IN = film.min_spacing_inches;
    const boxRef = useRef(null);
    const sheetRef = useRef(null);
    const [box, setBox] = useState({ w: 0, h: 0 });
    const drag = useRef(null);

    useLayoutEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
        ro.observe(el);
        setBox({ w: el.clientWidth, h: el.clientHeight });
        return () => ro.disconnect();
    }, []);

    const fitPpi = box.w ? Math.max(4, Math.min((box.w - 130) / SHEET_WIDTH_IN, (box.h - 200) / Math.max(lengthIn, 12))) : 0;
    const ppi = fitPpi * zoom;
    const sheetW = SHEET_WIDTH_IN * ppi;
    const sheetH = lengthIn * ppi;
    const margin = ((SHEET_WIDTH_IN - PRINTABLE_WIDTH_IN) / 2) * ppi;

    useLayoutEffect(() => { onFitChange?.(fitPpi); }, [fitPpi]); // eslint-disable-line react-hooks/exhaustive-deps

    const toInches = (px) => px / ppi;

    const pointer = (e) => (e.touches ? e.touches[0] : e);

    const startDrag = (e, item, mode) => {
        e.stopPropagation();
        e.preventDefault();
        const p = pointer(e);
        setSelected(item.uid);
        onDragStart?.();
        drag.current = { mode, uid: item.uid, sx: p.clientX, sy: p.clientY, start: { ...item } };
    };

    useLayoutEffect(() => {
        const move = (e) => {
            const d = drag.current;
            if (!d) return;
            const p = pointer(e);
            const dxIn = toInches(p.clientX - d.sx);
            const dyIn = toInches(p.clientY - d.sy);
            if (d.mode === "move") {
                let x = snap(d.start.x + dxIn);
                let y = snap(d.start.y + dyIn);
                const b = aabb(d.start);
                // snap against neighbours (edge + required spacing)
                items.filter((it) => it.uid !== d.uid).forEach((it) => {
                    const ob = aabb(it);
                    const cands = [it.x, it.x + ob.w + SPACING_IN, it.x - b.w - SPACING_IN];
                    cands.forEach((c) => { if (Math.abs(c - x) < 0.2) x = Math.round(c * 100) / 100; });
                    const yc = [it.y, it.y + ob.h + SPACING_IN, it.y - b.h - SPACING_IN];
                    yc.forEach((c) => { if (Math.abs(c - y) < 0.2) y = Math.round(c * 100) / 100; });
                });
                onLiveChange(d.uid, { x: Math.max(0, Math.min(x, PRINTABLE_WIDTH_IN - b.w)), y: Math.max(0, y) });
            } else if (d.mode === "resize") {
                const w = Math.max(0.5, Math.min(PRINTABLE_WIDTH_IN, snap(d.start.wIn + dxIn, 0.1)));
                onLiveChange(d.uid, { wIn: w, hIn: Math.round((w / d.start.ratio) * 100) / 100 });
            } else if (d.mode === "rotate") {
                const rect = sheetRef.current?.getBoundingClientRect();
                if (!rect) return;
                const cx = rect.left + (margin + (d.start.x + aabb(d.start).w / 2) * ppi);
                const cy = rect.top + (d.start.y + aabb(d.start).h / 2) * ppi;
                let deg = (Math.atan2(p.clientY - cy, p.clientX - cx) * 180) / Math.PI + 90;
                deg = (Math.round(deg) + 360) % 360;
                [0, 90, 180, 270].forEach((k) => { if (Math.abs(deg - k) < 7) deg = k; });
                onLiveChange(d.uid, { rot: deg });
            }
        };
        const up = () => { drag.current = null; };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        window.addEventListener("touchmove", move, { passive: false });
        window.addEventListener("touchend", up);
        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchmove", move);
            window.removeEventListener("touchend", up);
        };
    }, [items, ppi, margin]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div ref={boxRef} className="relative h-full w-full overflow-auto" data-testid="gang-sheet-canvas"
            onMouseDown={() => setSelected(null)}>
            <div className="pointer-events-none absolute inset-0 opacity-60"
                style={{ backgroundImage: "linear-gradient(rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.06) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

            <div className="relative mx-auto w-max px-14 pb-8 pt-20">
                <div ref={sheetRef} className="relative bg-white shadow-[0_30px_60px_-24px_rgba(15,23,42,0.45)] ring-1 ring-black/10"
                    style={{ width: sheetW, height: sheetH }} data-testid="gang-sheet">
                    <div className="absolute inset-y-0 border-x border-dashed border-brand-green/60" style={{ left: margin, right: margin }} data-testid="printable-guide" />
                    <div className="absolute" style={{ left: margin, top: 0, width: PRINTABLE_WIDTH_IN * ppi, height: sheetH }}>
                        <Ruler ppi={ppi} lengthIn={lengthIn} printW={PRINTABLE_WIDTH_IN} />
                        {items.map((it) => {
                            const b = aabb(it);
                            const bad = conflicts.includes(it.uid) || oob.includes(it.uid);
                            const isSel = selected === it.uid;
                            return (
                                <div key={it.uid} className="absolute" style={{ left: it.x * ppi, top: it.y * ppi, width: b.w * ppi, height: b.h * ppi }}>
                                    <img src={it.url} alt={it.name} draggable={false}
                                        onMouseDown={(e) => startDrag(e, it, "move")} onTouchStart={(e) => startDrag(e, it, "move")}
                                        className="absolute left-1/2 top-1/2 cursor-move select-none object-contain"
                                        style={{ width: it.wIn * ppi, height: it.hIn * ppi, transform: `translate(-50%,-50%) rotate(${it.rot || 0}deg)` }}
                                        data-testid="gang-sheet-item" data-uid={it.uid} />
                                    <div className={`pointer-events-none absolute inset-0 border ${bad ? "border-red-500" : isSel ? "border-brand-green" : "border-transparent"}`} />
                                    {isSel && (
                                        <>
                                            <span onMouseDown={(e) => startDrag(e, it, "resize")} onTouchStart={(e) => startDrag(e, it, "resize")}
                                                className="absolute -bottom-1.5 -right-1.5 h-3.5 w-3.5 cursor-nwse-resize rounded-sm border border-black/60 bg-brand-green"
                                                data-testid="handle-resize" />
                                            <span onMouseDown={(e) => startDrag(e, it, "rotate")} onTouchStart={(e) => startDrag(e, it, "rotate")}
                                                className="absolute -top-6 left-1/2 h-3.5 w-3.5 -translate-x-1/2 cursor-grab rounded-full border border-black/60 bg-brand-green"
                                                data-testid="handle-rotate" />
                                            <span className="absolute -bottom-6 left-0 whitespace-nowrap rounded bg-black/80 px-1.5 py-0.5 font-mono text-[11px] text-brand-green">
                                                {it.wIn.toFixed(1)}" × {it.hIn.toFixed(1)}"
                                            </span>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {!items.length && (
                        <p className="absolute inset-0 flex items-center justify-center px-6 text-center font-mono text-[11px] uppercase tracking-widest text-black/30">
                            Upload artwork — we auto-nest it, then drag, resize and rotate anything
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
